<template>
	<div class="relative w-full">
		<div v-if="!showPayPal || !showPayLater" class="absolute inset-0 flex flex-col gap-2">
			<div v-if="!showPayPal" class="w-full min-h-[40px] border border-ihh-gray/40 rounded flex items-center justify-center bg-white z-10">
				<Spinner :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
			</div>
			<div v-if="!showPayLater" class="w-full min-h-[40px] border border-ihh-gray/40 rounded flex items-center justify-center bg-white z-10">
				<Spinner :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
			</div>
		</div>
		<div
			:class="{
				'space-y-2': layout === 'column',
				'flex flex-row gap-x-2': layout === 'row',
				'flex-1': layout === 'row',
			}"
		>
			<div
				:class="{
					'braintree-paypal-container': true,
					'flex-1': layout === 'row',
				}"
			>
				<div class="braintree-paypal-inner-container">
					<div id="paypal-button" class="w-full min-h-[40px]"></div>
				</div>
			</div>
			<div
				:class="{
					'braintree-paypal-container': true,
					'flex-1': layout === 'row',
				}"
			>
				<div class="braintree-paypal-inner-container">
					<div id="paylater-button" class="w-full min-h-[40px]"></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import { useCheckoutStore } from '../../stores/store.js'
import helpers from '../../helpers/checkout.js'
import braintree from 'braintree-web'
import Swal from 'sweetalert2'
import { Country, State } from 'country-state-city'
import Spinner from '../layout/spinner.vue'
import * as Sentry from '@sentry/vue'

export default {
	name: 'PaypalButton',
	components: { Spinner },
	inject: ['store'],
	props: {
		layout: {
			type: String,
			default: 'column',
			validator: (value) => ['column', 'row'].includes(value),
		},
		messages: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			checkout: useCheckoutStore(),
			clientToken: null,
			// amount: null,
			shippingAddress: null,
			clientInstance: null,
			paypalInstance: null,
			paypalCheckoutInstance: null,
			nonce: null,
			payPalShippingAddress: null,
			payPalEmail: null,
			showPayPal: false,
			showPayLater: false,
			messageInstance: null,
			isProcessing: false, // Flag to prevent duplicate transactions
		}
	},

	created() {
		// Component created, but don't cleanup here
	},

	async mounted() {
		await this.$nextTick()
		await this.fetchClientToken()
		await this.setupBraintreePayPal()
	},

	beforeUnmount() {
		// Ensure proper cleanup when component is unmounted
		this.cleanupPayPal()
	},

	methods: {
		async fetchClientToken() {
			try {
				console.log('Fetching the client token for PayPal...')
				const response = await axios.get(`${this.store.api}/controllers/braintree/clientToken`)

				if (!response.data.success) {
					console.error('Failed to fetch client token:', response.data.message)
					return
				}

				this.clientToken = response.data.token
			} catch (error) {
				console.error('Error fetching client token:', error)
			}
		},

		async waitForElement(selector, maxAttempts = 50) {
			for (let attempt = 0; attempt < maxAttempts; attempt++) {
				const element = document.querySelector(selector)
				if (element) return true
				await new Promise((resolve) => setTimeout(resolve, 100))
			}
			return false
		},

		async cleanupPayPal() {
			try {
				// Reset processing flag when cleaning up
				this.isProcessing = false
				// Clean up PayPal instances
				if (this.paypalCheckoutInstance) {
					await this.paypalCheckoutInstance.teardown()
					this.paypalCheckoutInstance = null
				}
				if (this.clientInstance) {
					await this.clientInstance.teardown()
					this.clientInstance = null
				}

				// Reset component state
				this.showPayPal = false
				this.showPayLater = false
				this.nonce = null
				this.payPalShippingAddress = null
				this.payPalEmail = null

				// Wait a moment for cleanup to complete
				await new Promise((resolve) => setTimeout(resolve, 100))
			} catch (error) {
				console.error('Error during PayPal cleanup:', error)
			}
		},

		async setupBraintreePayPal() {
			try {
				// Ensure cleanup before setup
				this.cleanupPayPal()
				const clientInstance = await braintree.client.create({
					authorization: this.clientToken,
				})
				this.clientInstance = clientInstance

				const paypalCheckoutInstance = await braintree.paypalCheckout.create({
					client: clientInstance,
				})
				this.paypalCheckoutInstance = paypalCheckoutInstance

				await paypalCheckoutInstance.loadPayPalSDK({
					currency: 'USD',
					intent: 'capture',
					commit: true,
					components: 'buttons,messages',
				})

				// Ensure Vue context is available
				const createOrder = this.createOrder.bind(this)
				// const onShippingChange = this.onShippingChange.bind(this)
				const onApprove = this.onApprove.bind(this)

				// Wait for PayPal button container
				if (await this.waitForElement('#paypal-button')) {
					// Render PayPal button
					await paypal
						.Buttons({
							fundingSource: paypal.FUNDING.PAYPAL,
							style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal', height: 40 },
							createOrder,
							// onShippingChange,
							onApprove,
							onCancel: () => {
								console.log('PayPal payment was cancelled by the user')
								this.isProcessing = false // Reset processing flag on cancel
							},
							onError: (err) => {
								console.error('PayPal error:', err)
								this.isProcessing = false // Reset processing flag on error
							},
						})
						.render('#paypal-button')
					this.showPayPal = true
				} else {
					console.error('PayPal button container not found after multiple attempts')
				}

				// Render PayLater button
				if (await this.waitForElement('#paylater-button')) {
					try {
						await paypal
							.Buttons({
								fundingSource: paypal.FUNDING.PAYLATER,
								style: { layout: 'vertical', color: 'gold', shape: 'rect', height: 40 },
								createOrder,
								// onShippingChange,
								onApprove,
								onCancel: () => {
									console.log('PayLater payment was cancelled by the user')
									this.isProcessing = false // Reset processing flag on cancel
								},
								onError: (err) => {
									console.error('PayLater error:', err)
									this.isProcessing = false // Reset processing flag on error
								},
							})
							.render('#paylater-button')
						this.showPayLater = true
					} catch (error) {
						// PayLater might not be available, this is ok
						console.log('PayLater not available:', error)
						this.showPayLater = true // Still mark as handled
					}
				}

				if (this.messages) {
					await this.loadMessage()
				}
			} catch (err) {
				console.error('Error setting up Braintree PayPal:', err)
			}
		},

		async createOrder() {
			// NOTE: commented out certificates and store credit check since no longer passing shipping to paypal

			// if (this.checkout.giftCertificateAmount > 0 || this.checkout.storeCredit > 0) {
			// 	return Swal.fire({
			// 		title: 'Discounts Warning',
			// 		text: 'Gift certificates and store credit cannot be used with PayPal. Please remove them from your cart or select another payment method.',
			// 		icon: 'warning',
			// 		confirmButtonText: 'OK',
			// 	})
			// }

			// if (!this.checkout.expressCheckoutValidated?.value) { // digital wallets hidden until checkout is validated
			// 	return Swal.fire({
			// 		title: 'Missing Required Fields',
			// 		text: this.checkout.expressCheckoutValidated?.message,
			// 		icon: 'warning',
			// 		confirmButtonText: 'OK',
			// 	})
			// }

			// Prevent duplicate payment processing
			if (this.isProcessing) {
				console.log('Payment processing already in progress, ignoring additional paysheet request')
				return
			}
			this.isProcessing = true
			try {
				return await this.paypalCheckoutInstance.createPayment({
					flow: 'checkout',
					amount: String(this.checkout.remainingBalance), // String(this.subTotal),
					currency: 'USD',
					intent: 'capture',
					// enableShippingAddress: true,
					// shippingAddressEditable: true,
				})
			} catch (error) {
				console.error('Error creating order:', error)
				this.isProcessing = false // Reset processing flag on error
				Sentry.withScope((scope) => {
					scope.setTag('mangoCart', this.store?.cart?.id)
					scope.setTag('bcCart', this.store?.cart?.bcId)
					scope.setExtra('action', 'paypal_createPayment')
					Sentry.captureException(error)
				})
				throw error
			}
		},

		// async onShippingChange(data) {
		// 	console.log('Shipping address or option changed:', data)

		// 	const selectedShipping = data.selected_shipping_option
		// 	if (selectedShipping?.id) {
		// 		this.checkout.shipping.selectedShipping = {
		// 			id: selectedShipping.id,
		// 			description: selectedShipping.label,
		// 			cost: parseFloat(selectedShipping.amount.value),
		// 		}

		// 		await helpers.selectShippingMethod()

		// 		if (this.checkout.shipping.shippingAddress?.postalCode === data.shipping_address?.postal_code) return
		// 	}

		// 	const updatedAddress = data.shipping_address
		// 	console.log('updatedAddress', updatedAddress)
		// 	const stateName = State.getStateByCodeAndCountry(updatedAddress?.state, updatedAddress?.country_code)?.name

		// 	// if (!updatedAddress?.country_code) {
		// 	//     // get country code by stateOrProvice

		// 	// }

		// 	this.checkout.shipping.shippingAddress = {
		// 		city: updatedAddress?.city,
		// 		stateOrProvince: stateName,
		// 		countryCode: updatedAddress?.country_code,
		// 		postalCode: updatedAddress?.postal_code,
		// 	}

		// 	await helpers.getShippingEstimates()
		// 	const shippingOptions = this.checkout.shipping.shippingOptions.map((option) => ({
		// 		id: option.id,
		// 		label: helpers.formatShippingDescription(option.description),
		// 		type: option.description.includes('Pickup') ? 'PICKUP' : 'SHIPPING',
		// 		selected: option.description.includes('Pickup'),
		// 		amount: { currency: 'USD', value: String(this.calculatedShippingCost(option.cost)) },
		// 	}))

		// 	const defaultMethod = shippingOptions.find((option) => option.selected)
		// 	this.checkout.shipping.selectedShipping = {
		// 		id: defaultMethod.id,
		// 		description: defaultMethod.label,
		// 		cost: parseFloat(defaultMethod.amount.value),
		// 	}

		// 	const updatePaymentData = {
		// 		paymentId: data.paymentId,
		// 		amount: String(this.grandTotal),
		// 		currency: 'USD',
		// 		shippingOptions,
		// 		handleExecuteError: true,
		// 		// amountBreakdown: {
		// 		// 	itemTotal: String(this.subTotal - this.store.cart.tax),
		// 		// 	shipping: String(this.checkout.shipping.selectedShipping?.cost || 0),
		// 		// 	tax: String(this.store.cart.tax),
		// 		// 	discount: String(this.checkout.giftCertificateAmount + this.checkout.storeCredit || 0),
		// 		// },
		// 	}

		// 	console.log('updatePaymentData', updatePaymentData)

		// 	return this.paypalCheckoutInstance.updatePayment(updatePaymentData)
		// },

		async onApprove(data) {
			console.log('Payment approved')
			// let updateShipping = false
			try {
				const payload = await new Promise((resolve, reject) => {
					this.paypalCheckoutInstance.tokenizePayment(data, (err, payload) => {
						if (err) reject(err)
						else resolve(payload)
					})
				})

				// const fullAddress = payload.details.shippingAddress
				// const stateName = State.getStateByCodeAndCountry(fullAddress?.state, fullAddress?.countryCode)?.name

				// if (!this.checkout.shipping.shippingAddress?.address1) {
				// 	updateShipping = true
				// }
				// console.log('updateShipping: ', updateShipping)

				// this.checkout.shipping.shippingAddress = {
				// 	firstName: payload.details?.firstName || '',
				// 	lastName: payload.details?.lastName || '',
				// 	email: payload.details.email,
				// 	address1: fullAddress.line1,
				// 	address2: fullAddress?.line2 || '',
				// 	city: fullAddress.city,
				// 	stateOrProvince: stateName,
				// 	countryCode: fullAddress.countryCode,
				// 	postalCode: fullAddress.postalCode,
				// }

				// if (!this.checkout.billing.billingAddress?.address1) {
				// 	this.checkout.billing.billingAddress = {
				// 		firstName: payload.details?.firstName || '',
				// 		lastName: payload.details?.lastName || '',
				// 		email: payload.details.email,
				// 		company: '',
				// 		address1: fullAddress.line1,
				// 		address2: fullAddress?.line2 || '',
				// 		city: fullAddress.city,
				// 		stateOrProvince: stateName,
				// 		postalCode: fullAddress.postalCode,
				// 		countryCode: fullAddress.countryCode,
				// 	}
				// }

				// console.log('billing address: ', this.checkout.billing.billingAddress)

				console.log('payload: ', payload)

				await this.completeOrder(payload.nonce, payload?.details?.email)
			} catch (error) {
				console.error('Error during payment tokenization:', error)
				this.isProcessing = false // Reset processing flag on error
			}
		},

		async completeOrder(nonce, billingEmail) {
			try {
				// If guest, add paypal email to billing address
				if (!this.store?.user?.id) {
					this.checkout.setBillingEmail(billingEmail)
				}
				const processPayment = await axios.post(`${this.store.api}/controllers/checkout/paypal`, {
					orderComments: this.checkout.orderDetails.comments,
					checkoutId: this.store.cart.bcId,
					billingAddress: this.checkout.billing.billingAddress,
					existingBcOrderId: this.checkout.bcOrderId,
					staffNote: this.store.cart?.staffNote || '',
					useStoreCredit: this.checkout.payment.useStoreCredit,
					certificate: this.store.cart?.certificate?.balance ? this.store.cart.certificate : null,
					nonce: nonce,
					amount: this.grandTotal,
				})

				console.log('processPayment: ', processPayment)

				if (!processPayment?.data?.success) {
					this.isProcessing = false // Reset processing flag on error
					Swal.fire({
						icon: 'error',
						title: processPayment?.data?.code || 'Error',
						text: processPayment?.data?.message || 'There was an error completing your order. Please try again.',
						confirmButtonText: 'OK',
					})
					return
				}

				this.checkout.resetShippingAddress()
				this.store.checkoutSuccess = true

				// Reset cart
				this.store.cart = { cartItems: [], certificates: [] }
				this.store.originalCart = { cartItems: [], certificates: [] }
				window.localStorage.removeItem('cart')

				// Guest Order Logic
				if (!this.store?.user?.id) {
					window.localStorage.setItem('guestOrder', JSON.stringify(processPayment.data.order))
				}

				this.$router.push(processPayment.data.redirectUrl)
			} catch (error) {
				console.error('Error completing PayPal order:', error)
				this.isProcessing = false // Reset processing flag on error
				Swal.fire({
					icon: 'error',
					title: 'Error Processing Checkout',
					text: error?.response?.data?.code
						? `${error?.response?.data?.code}: ${error?.response?.data?.message}`
						: error?.message || 'There was an error completing your order. Please try again.',
					confirmButtonText: 'OK',
				})
			}
		},

		async loadMessage() {
			try {
				const messageInstance = window.paypal.Messages({
					amount: this.store?.cart?.total,
					style: {
						layout: 'text',
						text: {
							size: 12,
							color: 'black',
						},
						logo: {
							type: 'inline',
							position: 'right',
						},
					},
				})

				messageInstance.render('#paylater-message')
			} catch (error) {
				console.error('Error loading PayPal message:', error)
			}
		},
		calculatedShippingCost(baseAmount) {
			let total
			if (this.checkout.couponType === 'free_shipping') {
				total = 0
			} else if (this.checkout.couponType === 'shipping_discount') {
				let discount = parseFloat(this.checkout.couponAmount)
				if (discount > baseAmount) discount = baseAmount
				total = baseAmount - discount
			} else {
				total = baseAmount
			}
			return Number(parseFloat(total).toFixed(2))
		},
	},

	computed: {
		subTotal() {
			return Number(parseFloat(this.store?.cart?.total).toFixed(2))
		},
		subTotalWithDiscounts() {
			// coupon discounts are already applied to the cart subTotal
			const total = this.subTotal - this.checkout.giftCertificateAmount - this.checkout.storeCredit
			return Number(parseFloat(total).toFixed(2))
		},
		grandTotal() {
			let total = this.subTotalWithDiscounts + (this.checkout.shipping.selectedShipping?.cost || 0)
			if (total <= 0) total = this.subTotal
			return Number(parseFloat(total).toFixed(2))
		},
	},
}
</script>

<style>
.braintree-paypal-inner-container {
	display: inline-block;
	position: relative;
	width: 100%;
	z-index: 1;
}

.braintree-paypal-container {
	max-height: 40px;
	width: 100%;
}

.braintree-paypal-inner-container > div {
	width: 100% !important;
	max-width: 100%;
}

.braintree-paypal-inner-container iframe {
	z-index: 1;
}

@media screen and (max-width: 800px) {
	.braintree-paypal-container {
		width: 100%;
	}

	.braintree-paypal-inner-container {
		width: 100%;
		min-width: 100% !important;
	}
}
</style>
